/**
 * Notifications Service
 * Per-org trigger-based notifications with color coding.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:14102/api/v1';
const API_BASE_URL = BASE_URL.replace(/\/api\/v1\/?$/, '');
const ACCESS_TOKEN_KEY = 'access_token';

function authHeader(): { Authorization: string } | undefined {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'error' | 'critical';
export type NotificationTriggerEvent =
  | 'user.created'
  | 'user.deleted'
  | 'login.failed'
  | 'login.locked'
  | 'settings.changed'
  | 'feature.toggled'
  | 'language.added'
  | 'org.created';

export interface NotificationCondition {
  field?: string;
  op?: 'eq' | 'neq' | 'contains' | 'gt' | 'lt';
  value?: unknown;
}

export interface NotificationRule {
  id: number;
  organization_id: number;
  name: string;
  description: string | null;
  trigger_event: NotificationTriggerEvent;
  condition: NotificationCondition;
  severity: NotificationSeverity;
  color: string;
  message_template: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationItem {
  id: number;
  organization_id: number;
  rule_id: number | null;
  trigger_event: NotificationTriggerEvent;
  severity: NotificationSeverity;
  color: string;
  message: string;
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

export interface CreateNotificationRuleDto {
  name: string;
  description?: string;
  trigger_event: NotificationTriggerEvent;
  condition?: NotificationCondition;
  severity: NotificationSeverity;
  color?: string;
  message_template: string;
  enabled?: boolean;
}

export interface UpdateNotificationRuleDto extends Partial<CreateNotificationRuleDto> {}

export interface EventCatalog {
  events: NotificationTriggerEvent[];
  severities: NotificationSeverity[];
  severity_colors: Record<NotificationSeverity, string>;
}

export const notificationsService = {
  async getEventCatalog(): Promise<EventCatalog> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/notification-rules/events`, {
      headers: authHeader(),
    });
    return res.data;
  },

  async listRules(): Promise<NotificationRule[]> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/notification-rules`, {
      headers: authHeader(),
    });
    return res.data;
  },

  async createRule(dto: CreateNotificationRuleDto): Promise<NotificationRule> {
    const res = await axios.post(`${API_BASE_URL}/api/v1/notification-rules`, dto, {
      headers: authHeader(),
    });
    return res.data;
  },

  async updateRule(id: number, dto: UpdateNotificationRuleDto): Promise<NotificationRule> {
    const res = await axios.put(`${API_BASE_URL}/api/v1/notification-rules/${id}`, dto, {
      headers: authHeader(),
    });
    return res.data;
  },

  async deleteRule(id: number): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/v1/notification-rules/${id}`, {
      headers: authHeader(),
    });
  },

  async list(opts: { unreadOnly?: boolean; limit?: number; offset?: number } = {}): Promise<{
    items: NotificationItem[];
    total: number;
  }> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/notifications`, {
      headers: authHeader(),
      params: {
        unreadOnly: opts.unreadOnly ? 'true' : undefined,
        limit: opts.limit,
        offset: opts.offset,
      },
    });
    return res.data;
  },

  async unreadCount(): Promise<number> {
    const res = await axios.get(`${API_BASE_URL}/api/v1/notifications/unread-count`, {
      headers: authHeader(),
    });
    return res.data?.count ?? 0;
  },

  async markRead(id: number): Promise<NotificationItem> {
    const res = await axios.post(
      `${API_BASE_URL}/api/v1/notifications/${id}/read`,
      {},
      { headers: authHeader() },
    );
    return res.data;
  },

  async markAllRead(): Promise<number> {
    const res = await axios.post(
      `${API_BASE_URL}/api/v1/notifications/read-all`,
      {},
      { headers: authHeader() },
    );
    return res.data?.updated ?? 0;
  },

  async deleteNotification(id: number): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/v1/notifications/${id}`, {
      headers: authHeader(),
    });
  },
};
